import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  email: "",
  company: "",
  country: "",
  phone: "",
  product_slug: "general",
  quantity: "",
  message: "",
};

export default function RfqForm({ products = [], defaultProduct = "general" }) {
  const [form, setForm] = useState({ ...EMPTY, product_slug: defaultProduct });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and requirement.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("failed");
      setDone(true);
      toast.success("Enquiry received — our export desk replies within 24 hours.");
    } catch {
      toast.error("Could not send your enquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="rfq-success"
        className="flex flex-col items-center gap-4 rounded-2xl border border-emerald-600/20 bg-sage/60 px-6 py-12 text-center"
      >
        <CheckCircle2 className="h-12 w-12 text-forest" />
        <div>
          <p className="font-display text-xl font-semibold text-ink">Enquiry received</p>
          <p className="mt-2 text-sm text-slate-600">
            Thank you, {form.name.split(" ")[0]}. Our export desk will respond with pricing,
            specifications and shipping terms within 24 hours.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="rfq-form">
      <div className="space-y-1.5">
        <Label htmlFor="rfq-name" className="text-xs font-semibold text-ink">
          Full Name *
        </Label>
        <Input
          id="rfq-name"
          data-testid="rfq-name-input"
          value={form.name}
          onChange={set("name")}
          placeholder="Jane Doe"
          className="rounded-xl"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="rfq-email" className="text-xs font-semibold text-ink">
          Business Email *
        </Label>
        <Input
          id="rfq-email"
          data-testid="rfq-email-input"
          type="email"
          value={form.email}
          onChange={set("email")}
          placeholder="jane@company.com"
          className="rounded-xl"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="rfq-company" className="text-xs font-semibold text-ink">
          Company
        </Label>
        <Input
          id="rfq-company"
          data-testid="rfq-company-input"
          value={form.company}
          onChange={set("company")}
          placeholder="Company Ltd."
          className="rounded-xl"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="rfq-country" className="text-xs font-semibold text-ink">
          Country
        </Label>
        <Input
          id="rfq-country"
          data-testid="rfq-country-input"
          value={form.country}
          onChange={set("country")}
          placeholder="United Arab Emirates"
          className="rounded-xl"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="rfq-phone" className="text-xs font-semibold text-ink">
          Phone / WhatsApp
        </Label>
        <Input
          id="rfq-phone"
          data-testid="rfq-phone-input"
          value={form.phone}
          onChange={set("phone")}
          placeholder="+971 …"
          className="rounded-xl"
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs font-semibold text-ink">Product Interest</Label>
        <Select
          value={form.product_slug}
          onValueChange={(v) => setForm((f) => ({ ...f, product_slug: v }))}
        >
          <SelectTrigger data-testid="rfq-product-select" className="rounded-xl">
            <SelectValue placeholder="Select a product" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="general" data-testid="rfq-product-general">
              General Enquiry
            </SelectItem>
            {products.map((p) => (
              <SelectItem key={p.slug} value={p.slug} data-testid={`rfq-product-${p.slug}`}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5 sm:col-span-2">
        <Label htmlFor="rfq-quantity" className="text-xs font-semibold text-ink">
          Estimated Quantity / Volume
        </Label>
        <Input
          id="rfq-quantity"
          data-testid="rfq-quantity-input"
          value={form.quantity}
          onChange={set("quantity")}
          placeholder="e.g. 2 × 20ft containers per month"
          className="rounded-xl"
        />
      </div>
      <div className="space-y-1.5 sm:col-span-2">
        <Label htmlFor="rfq-message" className="text-xs font-semibold text-ink">
          Your Requirement *
        </Label>
        <Textarea
          id="rfq-message"
          data-testid="rfq-message-input"
          value={form.message}
          onChange={set("message")}
          placeholder="Grades, specs, destination port, packing preferences…"
          rows={4}
          className="rounded-xl"
        />
      </div>
      <button
        type="submit"
        data-testid="rfq-submit-button"
        disabled={loading}
        className="flex items-center justify-center gap-2 rounded-full bg-forest px-6 py-3.5 text-sm font-semibold text-white transition-all hover:bg-forest-hover disabled:opacity-60 sm:col-span-2"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {loading ? "Sending…" : "Send Enquiry"}
      </button>
    </form>
  );
}
